import { LayoutGroup, motion } from 'framer-motion';
import {
  Box,
  CalendarRange,
  ClipboardSignature,
  FileBarChart2,
  FolderOpen,
  Inbox,
  KanbanSquare,
  LayoutDashboard,
  Milestone,
  Network,
  Pencil,
  Rows3,
  ShieldCheck,
  Sigma,
  TrendingUp,
  UserCheck,
  Users,
  Wrench,
} from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import type { EpcProject } from '../../../services/epcProjectService';
import { useEpcProjectDetail } from './EpcProjectDetailContext';
import { EPC_THEME_STYLES, getEpcVisualTheme } from '../utils/epcProjectVisual';

export type EpcProjectHeroCardProps = {
  project: EpcProject;
  hasSchedule: boolean;
};

const TAB_LINK =
  'relative flex min-h-[2.75rem] min-w-[7.5rem] w-full items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-[10px] px-2.5 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors duration-200 sm:min-h-0 sm:min-w-0 sm:px-4 sm:text-xs sm:tracking-[0.14em]';

type TabSpec = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  title?: string;
};

export default function EpcProjectHeroCard({ project }: EpcProjectHeroCardProps) {
  const { pathname } = useLocation();
  const { canUpdate, myCap, mine } = useEpcProjectDetail();
  const theme = getEpcVisualTheme(project);
  const styles = EPC_THEME_STYLES[theme];
  const onSchedulePath = /\/schedule\/?$/.test(pathname);
  const projectId = String(project._id ?? project.id ?? '');

  /**
   * Project Head is folded into `governance` myCap by EpcProjectDetailContext, so we
   * distinguish PH from the wider governance pool by inspecting the user's membership row.
   * A user who holds a governance role (e.g. EPC head, PMO, project coordinator, super
   * admin) gets the 5-tab governance set; only `project_head` gets the PH 8-tab set.
   */
  const isProjectHead = mine?.role === 'project_head' && myCap === 'governance';

  const phTabs: TabSpec[] = [
    { to: 'charter', label: 'Charter', icon: ClipboardSignature, title: 'Project charter and approval' },
    { to: 'departments', label: 'Departments', icon: Network, title: 'Department leads and contacts' },
    {
      to: 'master-schedule',
      label: 'Master Schedule',
      icon: CalendarRange,
      title: 'Top-level project schedule',
    },
    { to: 'milestones', label: 'Milestones', icon: Milestone, title: 'Major milestones and gates' },
    { to: 'risks', label: 'Risks', icon: ShieldCheck, title: 'Project risk register' },
    { to: 'resource-plan', label: 'Resource Plan', icon: Sigma, title: 'Manpower and resource plan' },
    {
      to: 'procurement-engineering',
      label: 'Procurement & Eng',
      icon: Wrench,
      title: 'Procurement and engineering rollup',
    },
    { to: 'reports', label: 'Reports', icon: FileBarChart2, title: 'Reports and exports' },
  ];

  const dhTabs: TabSpec[] = [
    { to: 'dh-overview', label: 'Overview', icon: LayoutDashboard, end: true, title: 'Department KPIs, manager health, milestones' },
    { to: 'work-package-inbox', label: 'Task Inbox', icon: Inbox, title: 'Tasks assigned to your departments by PMO' },
    { to: 'team', label: 'Team', icon: Users, title: 'Managers and engineers in your department' },
    { to: 'department-schedule', label: 'Dept Schedule', icon: CalendarRange, title: 'Schedule scoped to your department' },
    { to: 'manager-tracking', label: 'Manager Tracking', icon: TrendingUp, title: 'Per-manager workload and performance' },
    { to: 'department-dpr', label: 'Dept DPR', icon: ClipboardSignature, title: 'Daily progress rollup with sign-off' },
    { to: 'quality-safety', label: 'Quality & Safety', icon: ShieldCheck, title: 'Quality and safety dashboards' },
    { to: 'reports', label: 'Reports', icon: FileBarChart2, title: 'Departmental reports' },
  ];

  const dmTabs: TabSpec[] = [
    {
      to: 'dpr',
      label: 'DPR',
      icon: ClipboardSignature,
      title: 'Daily command surface — schedule scope, day plans, progress, approvals',
    },
    { to: 'board', label: 'Board', icon: KanbanSquare, title: 'Engineer-wise timeline board' },
  ];

  const engineerTabs: TabSpec[] = [
    {
      to: `/epc/projects/${projectId}/workspace/pulse`,
      label: 'My workspace',
      icon: UserCheck,
      title: 'Your tasks, your team, your responsibilities on this project',
    },
    {
      to: 'project-3d',
      label: '3D overview',
      icon: Box,
      title: 'Visual progress of the project',
    },
    {
      to: 'documents',
      label: 'Documents',
      icon: FolderOpen,
      title: 'Project files',
    },
  ];

  const governanceTabs: TabSpec[] = [
    { to: 'overview', label: 'Overview', icon: LayoutDashboard, end: true, title: 'Summary and master data' },
    { to: 'team', label: 'Team hierarchy', icon: Users, title: 'Departments and reporting chain' },
    { to: 'schedule', label: 'Schedule', icon: Rows3, title: 'WBS, timeline, MPP import, and workspace tools' },
    {
      to: 'documents',
      label: 'Document repository',
      icon: FolderOpen,
      title: 'Project DPR files, filters, and department workspaces',
    },
    // {
    //   to: 'project-3d',
    //   label: '3D overview',
    //   icon: Box,
    //   title: '3D abstract project architecture from schedule tasks',
    // },
  ];

  // Assumes a RESOLVED myCap: EpcProjectDetailShell gates rendering until
  // memberships settle, so the governance fallthrough is only reachable for
  // governance/superadmin (and admin carve-out) callers — never mid-load.
  const tabs: TabSpec[] = isProjectHead
    ? phTabs
    : myCap === 'dept_head'
      ? dhTabs
      : myCap === 'manager'
        ? dmTabs
        : myCap === 'engineer'
          ? engineerTabs
          : governanceTabs;

  const colsClass =
    tabs.length === 9
      ? 'sm:grid-cols-9'
      : tabs.length === 8
        ? 'sm:grid-cols-8'
        : tabs.length === 7
        ? 'sm:grid-cols-7'
        : tabs.length === 6
          ? 'sm:grid-cols-6'
          : tabs.length === 5
            ? 'sm:grid-cols-5'
            : tabs.length === 4
              ? 'sm:grid-cols-4'
              : 'sm:grid-cols-3';

  return (
    <div className="relative w-full min-w-0 border-b border-[rgba(11,59,46,0.14)] bg-[#F8F2E2] px-3 py-3 sm:px-5 sm:py-3.5 md:px-6">
      <div className="mb-2.5 flex items-center justify-between gap-2 sm:mb-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C9A961] sm:text-[11px]">
          Project workspace
        </p>
        {canUpdate && projectId ? (
          <Link
            to={`/epc/projects/${projectId}/edit`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[rgba(11,59,46,0.2)] bg-[#fcfaf3] px-2.5 py-1.5 text-[11px] font-semibold text-[#1F5A47] shadow-sm transition hover:bg-[rgba(31,90,71,0.08)]"
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={2.4} />
            Edit project
          </Link>
        ) : null}
      </div>
      <LayoutGroup id="epc-project-main-tabs">
        <nav
          className="-mx-1 w-full max-w-full overflow-x-auto pb-1 scrollbar-thin sm:mx-0 sm:overflow-visible sm:pb-0"
          aria-label="Project workspace"
        >
          <div
            className={`grid w-full min-w-min grid-flow-col auto-cols-[minmax(7.5rem,1fr)] gap-0 rounded-2xl border border-[rgba(11,59,46,0.12)] bg-[rgba(11,59,46,0.05)] p-1 shadow-inner sm:auto-cols-fr sm:grid-flow-row ${colsClass} sm:rounded-[14px]`}
            role="tablist"
          >
            {tabs.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                title={t.title}
                className={({ isActive }) => {
                  const scheduleActive = t.to === 'schedule' && onSchedulePath;
                  const active = t.to === 'schedule' ? scheduleActive : isActive;
                  return `${TAB_LINK} ${active ? 'text-white' : 'text-[#2A1F17] hover:text-[#0B3B2E]'}`;
                }}
              >
                {({ isActive }) => {
                  const scheduleActive = t.to === 'schedule' && onSchedulePath;
                  const active = t.to === 'schedule' ? scheduleActive : isActive;
                  return (
                    <>
                      {active ? (
                        <motion.span
                          layout
                          layoutId="epc-project-tab-pill"
                          className="absolute inset-0 rounded-[10px] shadow-md"
                          style={{
                            background: `linear-gradient(135deg, ${styles.accent}, ${styles.accent}dd)`,
                            boxShadow: `0 10px 26px -14px ${styles.accent}`,
                          }}
                          transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                        />
                      ) : null}
                      <span className="relative z-10 flex items-center gap-2">
                        <t.icon className="h-3.5 w-3.5 shrink-0 opacity-95 sm:h-4 sm:w-4" strokeWidth={2} aria-hidden />
                        <span>{t.label}</span>
                      </span>
                    </>
                  );
                }}
              </NavLink>
            ))}
          </div>
        </nav>
      </LayoutGroup>
    </div>
  );
}
